// src/components/DashboardTopbar.tsx
import React, { Fragment } from "react";
import { Menu, Transition } from "@headlessui/react";
import { Bell, ChevronDown, User, Settings, LogOut } from "lucide-react";

export interface DashboardTopbarProps {
  /** URL of your restaurant’s logo */
  logoUrl: string;
  /** Restaurant or store name */
  name: string;
  /** Restaurant address or subtitle */
  address: string;
}

export default function DashboardTopbar({
  logoUrl,
  name,
  address,
}: DashboardTopbarProps) {
  return (
    <header className="flex items-center justify-between bg-white px-6 py-4 shadow">
      {/* Left: Logo + Store Info */}
      <div className="flex items-center gap-4">
        <img
          src={logoUrl}
          alt="Logo"
          className="h-10 w-10 object-contain rounded"
        />
        <div className="flex flex-col">
          <span className="text-lg font-semibold text-gray-800">{name}</span>
          <span className="text-sm text-gray-500">{address}</span>
        </div>
      </div>

      {/* Right: Notifications + Avatar Dropdown */}
      <div className="flex items-center gap-4">
        <button className="relative p-2 rounded-full hover:bg-gray-100 transition">
          <Bell className="text-gray-600" />
          <span className="absolute top-1 right-1 block h-2 w-2 bg-red-500 rounded-full" />
        </button>

        <Menu as="div" className="relative">
          <Menu.Button className="flex items-center gap-2 focus:outline-none">
            <div className="h-8 w-8 rounded-full border-2 border-gray-200 overflow-hidden">
              {/* Placeholder icon; replace with <img src={avatarUrl} /> if you have a user avatar URL */}
              <User className="w-full h-full text-gray-400 p-1" />
            </div>
            <ChevronDown className="text-gray-600" />
          </Menu.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 mt-2 w-40 bg-white shadow-lg ring-1 ring-black ring-opacity-5 rounded-md py-1 z-10">
              <Menu.Item>
                {({ active }) => (
                  <button
                    className={`flex items-center gap-2 w-full px-4 py-2 text-sm ${
                      active ? "bg-gray-100" : ""
                    }`}
                  >
                    <User className="w-4 h-4 text-gray-600" />
                    Profile
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    className={`flex items-center gap-2 w-full px-4 py-2 text-sm ${
                      active ? "bg-gray-100" : ""
                    }`}
                  >
                    <Settings className="w-4 h-4 text-gray-600" />
                    Settings
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    className={`flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 ${
                      active ? "bg-gray-100" : ""
                    }`}
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                )}
              </Menu.Item>
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
    </header>
  );
}
